// src/App.jsx

import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import Login from './components/Login';
import SignUp from './components/SignUp';
import AuthCallback from './components/AuthCallback';
import AtlasBriefSharedPage from './components/atlas/AtlasBriefSharedPage';
import MainLayout from './components/MainLayout';
import api from './api';
import { auth } from './firebase';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { consumeRedirectPending } from './auth/googleSignIn';
import { clearPendingSignup, pendingSignupFor, signupPayload } from './auth/pendingSignup';
import logger from './utils/logger';
import mixpanel from './utils/mixpanel';
import { installMobileViewport } from './utils/mobileViewport';
import {
  ATLAS_PLAYWRIGHT_AUTH_DISABLED,
  getPlaywrightClients,
  getPlaywrightCoverageState,
  getPlaywrightSelectedClient,
  getPlaywrightUser,
} from './devAuth';
import './sentry';
import './theme.css';
import './App.css';

const COVERAGE_STATUSES = ['active', 'demo', 'inactive'];
const AUTH_ENTRY_PATHS = new Set(['/login', '/sign-up', '/auth/callback']);

function isAuthEntryPath(pathname) {
  return AUTH_ENTRY_PATHS.has(pathname);
}

function normalizeFiveDigitZip(rawZip) {
  const digits = String(rawZip ?? '').replace(/\D/g, '').slice(0, 5);
  return digits.length === 5 ? digits : '';
}

function normalizeZipList(rawZipCodes = []) {
  return [...new Set(
    (Array.isArray(rawZipCodes) ? rawZipCodes : [])
      .map((zipCode) => normalizeFiveDigitZip(zipCode))
      .filter(Boolean)
  )].sort((left, right) => left.localeCompare(right));
}

function normalizeCoverageStatus(rawStatus) {
  const normalized = String(rawStatus ?? '').trim().toLowerCase();
  return COVERAGE_STATUSES.includes(normalized) ? normalized : 'inactive';
}

function getAuthErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Unknown authentication error.'
  );
}

// Theme Context
const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {}
});

export const useTheme = () => useContext(ThemeContext);

// Redirect legacy /atlas/share/:shareId to /share/:shareId
function LegacyShareRedirect() {
  const { shareId } = useParams();
  return <Navigate to={`/share/${shareId}`} replace />;
}

const App = () => {
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [coverageState, setCoverageState] = useState(null);
  const [selectedZip, setSelectedZip] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBootstrapLoading, setIsBootstrapLoading] = useState(false);
  const [hasCompletedInitialBootstrap, setHasCompletedInitialBootstrap] = useState(false);
  const [hasLoadedAdminClients, setHasLoadedAdminClients] = useState(false);
  
  // Theme state - dark mode is disabled for now, always use light
  const [theme] = useState('light');

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    localStorage.setItem('theme', 'light');
  }, []);

  // Publishes --app-height / --vh / --keyboard-inset off the visual viewport so
  // mobile browser chrome and the on-screen keyboard stop covering content.
  useEffect(() => installMobileViewport(), []);

  // Theme toggle disabled for now
  const toggleTheme = useCallback(() => {
    // Dark mode is paused - do nothing
  }, []);

  const setTheme = useCallback(() => {
    // Dark mode is paused - do nothing
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const buildZipContextMap = useCallback((contexts = []) => {
    const map = {};
    contexts.forEach((context) => {
      const zipCode = normalizeFiveDigitZip(context?.zip_code);
      if (!zipCode) {
        return;
      }
      map[zipCode] = {
        ...context,
        zip_code: zipCode,
      };
    });
    return map;
  }, []);

  const applyBootstrapPayload = useCallback((payload) => {
    const normalizedAccessible = normalizeZipList(payload?.accessible_zip_codes);
    const companyZipStatusMap = {};

    (Array.isArray(payload?.company_zip_coverage) ? payload.company_zip_coverage : []).forEach((coverageRow) => {
      const zipCode = normalizeFiveDigitZip(coverageRow?.zip_code);
      if (!zipCode) {
        return;
      }
      companyZipStatusMap[zipCode] = normalizeCoverageStatus(coverageRow?.status);
    });

    COVERAGE_STATUSES.forEach((status) => {
      const zipCodes = payload?.company_zip_codes_by_status?.[status];
      normalizeZipList(zipCodes).forEach((zipCode) => {
        companyZipStatusMap[zipCode] = status;
      });
    });

    normalizeZipList(payload?.company_zip_codes).forEach((zipCode) => {
      companyZipStatusMap[zipCode] = 'active';
    });

    const normalizedCompanyCoverage = Object.entries(companyZipStatusMap)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([zip_code, status]) => ({ zip_code, status }));

    const normalizedCompanyByStatus = COVERAGE_STATUSES.reduce((accumulator, status) => {
      accumulator[status] = normalizedCompanyCoverage
        .filter((coverageRow) => coverageRow.status === status)
        .map((coverageRow) => coverageRow.zip_code);
      return accumulator;
    }, {});

    const normalizedCompany = normalizedCompanyByStatus.active;

    const nextCoverageState = {
      ...payload,
      accessibleZipCodes: normalizedAccessible,
      companyZipCodes: normalizedCompany,
      companyZipCoverage: normalizedCompanyCoverage,
      companyZipStatusMap,
      companyZipCodesByStatus: normalizedCompanyByStatus,
      zipContextMap: buildZipContextMap(payload?.zip_contexts || []),
      companyZipContextMap: buildZipContextMap(payload?.company_zip_contexts || []),
    };

    setCoverageState(nextCoverageState);
    setSelectedZip((currentZip) => {
      if (currentZip && normalizedAccessible.includes(currentZip)) {
        return currentZip;
      }
      return nextCoverageState.default_zip || normalizedAccessible[0] || null;
    });
    return nextCoverageState;
  }, [buildZipContextMap]);

  const fetchSessionBootstrap = useCallback(async (clientId = null, options = {}) => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      const payload = getPlaywrightCoverageState();
      applyBootstrapPayload(payload);
      return payload;
    }

    try {
      setIsBootstrapLoading(true);
      const params = clientId ? { client_id: clientId } : {};
      const companyId = options?.companyId || options?.company_id || null;
      if (companyId) {
        params.company_id = companyId;
      }
      const response = await api.get('/api/session-bootstrap', { params });
      applyBootstrapPayload(response.data);
      return response.data;
    } catch (error) {
      logger.error('Error fetching session bootstrap:', error);
      setCoverageState(null);
      setSelectedZip(null);
      throw error;
    } finally {
      setIsBootstrapLoading(false);
    }
  }, [applyBootstrapPayload]);

  useEffect(() => {
    // log page view event
    mixpanel.track('Page Viewed', {
      path: location.pathname,
      user: user
    });
  }, [location.pathname, user]);

  // Ref to track the authenticated user email to prevent re-authentication loops
  const authenticatedEmailRef = useRef(null);
  const currentUserRef = useRef(null);
  const attemptedCoverageReconciliationsRef = useRef(new Set());
  // One-way latch: once the app tree has rendered, the full-screen loading
  // spinner must never replace it again (see the loading gate below).
  const hasRenderedAppRef = useRef(false);

  useEffect(() => {
    currentUserRef.current = user;
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    let redirectResultSettled = false;
    let authStateSettled = false;

    const finishLoadingIfReady = () => {
      if (!isMounted || !redirectResultSettled || !authStateSettled) {
        return;
      }
      setIsLoading(false);
    };

    const markAuthStateSettled = () => {
      authStateSettled = true;
      finishLoadingIfReady();
    };

    const redirectAuthenticatedUser = () => {
      // OAuth redirects can land back on auth entry screens before React's route
      // guard swaps views, so move authenticated users to the app explicitly.
      if (isAuthEntryPath(window.location.pathname)) {
        navigate({ pathname: '/', search: window.location.search }, { replace: true });
      }
    };

    const clearAppUser = () => {
      authenticatedEmailRef.current = null;
      currentUserRef.current = null;
      hasRenderedAppRef.current = false;
      if (isMounted) {
        setUser(null);
        setHasCompletedInitialBootstrap(false);
        setHasLoadedAdminClients(false);
      }
    };

    let activeSyncKey = null;
    let activeSyncPromise = null;

    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      const nextUser = getPlaywrightUser();
      authenticatedEmailRef.current = nextUser.email;
      currentUserRef.current = nextUser;
      if (isMounted) {
        setUser(nextUser);
        setClients(getPlaywrightClients());
        setSelectedClient(getPlaywrightSelectedClient());
        setCoverageState(getPlaywrightCoverageState());
        setSelectedZip(null);
        setIsBootstrapLoading(false);
        setHasCompletedInitialBootstrap(true);
        setHasLoadedAdminClients(true);
        setIsLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    const getAuthSyncKey = (firebaseUser) => {
      if (!firebaseUser) {
        return 'signed-out';
      }
      return `${firebaseUser.uid || ''}:${firebaseUser.email || ''}`;
    };

    const syncFirebaseUser = (firebaseUser) => {
      const syncKey = getAuthSyncKey(firebaseUser);
      if (activeSyncPromise && activeSyncKey === syncKey) {
        return activeSyncPromise;
      }

      authStateSettled = false;

      const syncPromise = (async () => {
        try {
          if (firebaseUser) {
            // Skip re-authentication only when the app user is already hydrated.
            // If a sign-in just happened, keep the route guarded until backend
            // session sync finishes so "/" cannot bounce back to "/login".
            if (authenticatedEmailRef.current === firebaseUser.email && currentUserRef.current) {
              redirectAuthenticatedUser();
              return;
            }

            hasRenderedAppRef.current = false;
            if (isMounted) {
              setIsLoading(true);
              setHasCompletedInitialBootstrap(false);
              setHasLoadedAdminClients(false);
              setCoverageState(null);
              setSelectedZip(null);
            }
            logger.log('Firebase user detected:', firebaseUser.email);

            const idToken = await firebaseUser.getIdToken();
            // A sign-up in progress: this call may be the one that creates the user, so it
            // carries the form's name, company, phone and consents.
            const pendingSignup = pendingSignupFor(firebaseUser);
            const response = await api.post(
              '/firebase-login',
              {
                idToken,
                name: firebaseUser.displayName || pendingSignup?.name || '',
                ...(pendingSignup ? { signup: signupPayload(pendingSignup) } : {}),
              },
              { withCredentials: true }
            );
            logger.log('Backend response:', response.data);
            if (pendingSignup && response.data.success) clearPendingSignup();

            if (response.data.success) {
              authenticatedEmailRef.current = firebaseUser.email;
              const nextUser = {
                id: response.data.user.id,
                email: response.data.user.email,
                name: response.data.user.name,
                isAdmin: response.data.user.is_admin
              };
              currentUserRef.current = nextUser;
              if (isMounted) {
                setUser(nextUser);
              }
              redirectAuthenticatedUser();
            } else {
              logger.log(
                'Backend rejected login, signing out:',
                response.data.message
              );
              clearAppUser();
              await auth.signOut();
            }
          } else {
            logger.log('No Firebase user detected. User is logged out.');
            clearAppUser();
          }
        } catch (error) {
          logger.error('Error during backend authentication:', error);
          alert(`Sign-in worked, but RealTorch could not create your app session: ${getAuthErrorMessage(error)}`);
          clearAppUser();
          try {
            await auth.signOut();
          } catch (signOutError) {
            logger.error('Error signing out after failed backend authentication:', signOutError);
          }
        }
      })().finally(() => {
        if (activeSyncPromise === syncPromise) {
          activeSyncKey = null;
          activeSyncPromise = null;
          markAuthStateSettled();
        }
      });

      activeSyncKey = syncKey;
      activeSyncPromise = syncPromise;
      return syncPromise;
    };

    // Process redirect result from signInWithRedirect (Google OAuth)
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          consumeRedirectPending();
          return syncFirebaseUser(result.user);
        }
        // We came back from a Google redirect with no credential and no error.
        // That means the browser dropped the pending auth state in transit, so
        // say so instead of silently re-rendering the login screen.
        if (consumeRedirectPending() && !auth.currentUser) {
          alert(
            'Google sign-in did not complete. Your browser blocked the sign-in data '
            + 'on the way back. Try again, or use "Continue with Email".'
          );
        }
        return null;
      })
      .catch((error) => {
        logger.error('Redirect result error:', error);
        if (error.code === 'auth/account-exists-with-different-credential') {
          alert('An account already exists with this email using a different sign-in method.');
        } else {
          alert(`Google sign-in could not finish: ${getAuthErrorMessage(error)}`);
        }
      })
      .finally(() => {
        redirectResultSettled = true;
        finishLoadingIfReady();
      });

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      syncFirebaseUser(firebaseUser);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [navigate]); // Removed location.pathname - auth state should be independent of route

  const refreshClients = useCallback(async () => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      const nextClients = getPlaywrightClients();
      setClients(nextClients);
      setSelectedClient(getPlaywrightSelectedClient());
      return nextClients;
    }

    if (!user?.isAdmin) {
      return [];
    }

    try {
      const response = await api.get('/api/clients', {
        withCredentials: true
      });
      if (response.status !== 200) {
        return [];
      }

      const nextClients = Array.isArray(response.data) ? response.data : [];
      setClients(nextClients);
      setSelectedClient((currentSelectedClient) => {
        if (currentSelectedClient !== null || nextClients.length === 0) {
          return currentSelectedClient;
        }

        // Default to the current admin user if they're in the list.
        const currentUserInList = nextClients.find(client => String(client.id) === String(user.id));
        return String((currentUserInList || nextClients[0]).id);
      });
      return nextClients;
    } catch (error) {
      logger.error('Error fetching clients:', error);
      return [];
    } finally {
      setHasLoadedAdminClients(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user || isLoading) return;
    refreshClients();
  }, [user, isLoading, refreshClients]);

  useEffect(() => {
    if (user) {
      // identify the user with mixpanel
      mixpanel.identify(user.email);
      mixpanel.people.set({
        $id: user.id,
        $email: user.email,
        $name: user.name,
        $is_admin: user.isAdmin,
        last_login: new Date().toISOString()
      });
    }
  }, [user]);

  const handleClientChange = async (clientId) => {
    logger.log(`Changing selected client to: ${clientId}`);
    setSelectedClient(clientId);
  };

  const handleZipChange = useCallback((zipCode) => {
    logger.log(`Changing selected ZIP to: ${zipCode}`);
    setSelectedZip(zipCode || null);
  }, []);

  const handleCoverageUpdate = useCallback(async ({
    addZipCodes = [],
    demoZipCodes = [],
    deactivateZipCodes = [],
  }) => {
    const body = {
      add_zip_codes: addZipCodes,
      demo_zip_codes: demoZipCodes,
      deactivate_zip_codes: deactivateZipCodes,
    };
    if (user?.isAdmin && selectedClient) {
      body.client_id = selectedClient;
    }

    const response = await api.post('/api/coverage/batch-update', body);
    applyBootstrapPayload(response.data);
    return response.data;
  }, [applyBootstrapPayload, selectedClient, user]);

  const handleSeatRequest = useCallback(async ({ additionalSeats }) => {
    const body = {
      additional_seats: additionalSeats,
    };
    if (user?.isAdmin && selectedClient) {
      body.client_id = selectedClient;
    }

    const response = await api.post('/api/company/seats/request', body);
    applyBootstrapPayload(response.data);
    return response.data;
  }, [applyBootstrapPayload, selectedClient, user]);

  const reconcilePendingCoverageCheckouts = useCallback(async ({
    companyId = null,
    clientId = null,
    force = false,
  } = {}) => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      return null;
    }

    const resolvedCompanyId = companyId || null;
    if (!resolvedCompanyId && !clientId) {
      return null;
    }

    const reconciliationKey = `${clientId || 'self'}:${resolvedCompanyId || 'none'}`;
    if (!force && attemptedCoverageReconciliationsRef.current.has(reconciliationKey)) {
      return null;
    }
    attemptedCoverageReconciliationsRef.current.add(reconciliationKey);

    try {
      const response = await api.post('/api/stripe/reconcile-company-checkouts', {
        company_id: resolvedCompanyId || undefined,
        client_id: clientId || undefined,
      });
      const payload = response?.data || {};
      const appliedCount = Array.isArray(payload.applied_change_ids) ? payload.applied_change_ids.length : 0;
      const expiredCount = Array.isArray(payload.expired_change_ids) ? payload.expired_change_ids.length : 0;

      if (appliedCount > 0 || expiredCount > 0) {
        await fetchSessionBootstrap(clientId);
      }

      return payload;
    } catch (error) {
      attemptedCoverageReconciliationsRef.current.delete(reconciliationKey);
      logger.error('Failed to reconcile pending company checkouts:', error);
      return null;
    }
  }, [fetchSessionBootstrap]);

  useEffect(() => {
    let cancelled = false;

    const fetchBootstrapData = async () => {
      if (!user || isLoading) return;
      if (user.isAdmin && !selectedClient) return;

      const clientId = user.isAdmin ? selectedClient : null;
      try {
        const bootstrapPayload = await fetchSessionBootstrap(clientId);
        await reconcilePendingCoverageCheckouts({
          clientId,
          companyId: bootstrapPayload?.selected_company_id || bootstrapPayload?.company_id || null,
        });
      } catch (error) {
        logger.error('Bootstrap fetch failed:', error);
      } finally {
        if (!cancelled) {
          setHasCompletedInitialBootstrap(true);
        }
      }
    };

    fetchBootstrapData();

    return () => {
      cancelled = true;
    };
  }, [user, isLoading, selectedClient, fetchSessionBootstrap, reconcilePendingCoverageCheckouts]);

  const handleLogout = async () => {
    logger.log('Logging out. Clearing all user-related states.');
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      navigate('/');
      return;
    }

    try {
      await auth.signOut();
      setUser(null);
      setClients([]);
      setSelectedClient(null);
      setCoverageState(null);
      setSelectedZip(null);
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed:', error);
      alert('An error occurred during logout. Please try again.');
    }
  };

  // Hold the spinner through initial hydration (auth -> admin clients ->
  // first bootstrap) so the app mounts exactly once, fully hydrated. The
  // admin clause releases when clients have loaded but none is selectable,
  // so an admin with no clients can't spin forever.
  const isWaitingForInitialBootstrap = Boolean(
    user
    && !hasCompletedInitialBootstrap
    && (!user.isAdmin || selectedClient || !hasLoadedAdminClients)
  );

  // One-way gate: once the app tree has rendered, never swap back to the
  // full-screen spinner — doing so unmounts MainLayout entirely (the visible
  // "refresh"). Later re-bootstraps update via props; the latch resets on
  // logout/login so a new session gets the clean hydration above.
  if (
    !hasRenderedAppRef.current
    && (isLoading || isWaitingForInitialBootstrap || (user && isBootstrapLoading && !coverageState))
  ) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }
  hasRenderedAppRef.current = true;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      <div className="app">
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <MainLayout
                  user={user}
                  clients={clients}
                  selectedClient={selectedClient}
                  onClientChange={handleClientChange}
                  coverageState={coverageState}
                  isBootstrapLoading={isBootstrapLoading}
                  selectedZip={selectedZip}
                  onZipChange={handleZipChange}
                  onCoverageUpdate={handleCoverageUpdate}
                  onCompanySetupComplete={applyBootstrapPayload}
                  onSeatRequest={handleSeatRequest}
                  onReconcilePendingCheckouts={reconcilePendingCoverageCheckouts}
                  onRefreshCoverageContext={fetchSessionBootstrap}
                  onRefreshClients={refreshClients}
                  onLogout={handleLogout}
                />
              ) : (
                <Navigate to={{ pathname: '/login', search: location.search }} replace />
              )
            }
          />
          <Route
            path="/c/:conversationId"
            element={
              user ? (
                <MainLayout
                  user={user}
                  clients={clients}
                  selectedClient={selectedClient}
                  onClientChange={handleClientChange}
                  coverageState={coverageState}
                  isBootstrapLoading={isBootstrapLoading}
                  selectedZip={selectedZip}
                  onZipChange={handleZipChange}
                  onCoverageUpdate={handleCoverageUpdate}
                  onCompanySetupComplete={applyBootstrapPayload}
                  onSeatRequest={handleSeatRequest}
                  onReconcilePendingCheckouts={reconcilePendingCoverageCheckouts}
                  onRefreshCoverageContext={fetchSessionBootstrap}
                  onRefreshClients={refreshClients}
                  onLogout={handleLogout}
                />
              ) : (
                <Navigate to={{ pathname: '/login', search: location.search }} replace />
              )
            }
          />
          <Route path="/login" element={user ? <Navigate to={{ pathname: '/', search: location.search }} replace /> : <Login />} />
          <Route path="/sign-up" element={user ? <Navigate to={{ pathname: '/', search: location.search }} replace /> : <SignUp />} />
          <Route path="/share/:shareId" element={<AtlasBriefSharedPage />} />
          <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
          {/* Legacy route: redirect /atlas/share to /share (atlas prefix is deprecated) */}
          <Route path="/atlas/share/:shareId" element={<LegacyShareRedirect />} />
        </Routes>
      </div>
    </ThemeContext.Provider>
  );
};

export default App;
